import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { API } from '../App';

/**
 * useBankFeedInsights
 * --------------------------------------------------------------------------
 * Phase 1 part 3 - the third (and final-for-now) Bank Feed data hook.
 *
 * Centralizes the "specialty" fetchers that don't fit in the read-only
 * reference hook (useBankFeedData) or the list-shaped data hook
 * (useBankFeedTransactions):
 *
 *   - AI correction history       (/ai-corrections/correction-history)
 *   - AI suggested corrections    (/ai-corrections/suggested-corrections)
 *   - AI training examples        (/ai-corrections/training-examples)
 *   - AI training stats           (/ai-corrections/training-stats)
 *   - Reconciliation summary      (/bank-reconciliation/summary/all)
 *   - Detected reversals          (POST /bank-reversals/detect-reversals)
 *   - Reversed transactions       (/bank-reversals/reversed-transactions)
 *
 * Each fetcher owns its own loading state where one already existed in the
 * parent page. All fetchers are mounted-ref guarded and fail silently -
 * these views are auxiliary and a transient network blip should not crash
 * the page.
 */
export function useBankFeedInsights() {
  const isMountedRef = useRef(true);

  const [correctionHistory, setCorrectionHistory] = useState([]);
  const [suggestedCorrections, setSuggestedCorrections] = useState([]);
  const [loadingCorrections, setLoadingCorrections] = useState(false);

  const [trainingExamples, setTrainingExamples] = useState([]);
  const [trainingStats, setTrainingStats] = useState(null);
  const [loadingTraining, setLoadingTraining] = useState(false);

  const [reconSummary, setReconSummary] = useState(null);

  const [detectedReversals, setDetectedReversals] = useState([]);
  const [loadingReversals, setLoadingReversals] = useState(false);
  const [reversedTransactions, setReversedTransactions] = useState([]);

  useEffect(() => {
    isMountedRef.current = true;
    return () => { isMountedRef.current = false; };
  }, []);

  const fetchCorrectionHistory = useCallback(async () => {
    if (!isMountedRef.current) return;
    try {
      const res = await axios.get(`${API}/ai-corrections/correction-history?limit=50`);
      if (isMountedRef.current) setCorrectionHistory(res.data.corrections || []);
    } catch {
      // Silent
    }
  }, []);

  const fetchSuggestedCorrections = useCallback(async () => {
    if (!isMountedRef.current) return;
    setLoadingCorrections(true);
    try {
      const res = await axios.get(`${API}/ai-corrections/suggested-corrections`);
      if (isMountedRef.current) setSuggestedCorrections(res.data.suggestions || []);
    } catch {
      // Silent
    } finally {
      if (isMountedRef.current) setLoadingCorrections(false);
    }
  }, []);

  const fetchTrainingExamples = useCallback(async () => {
    if (!isMountedRef.current) return;
    setLoadingTraining(true);
    try {
      const res = await axios.get(`${API}/ai-corrections/training-examples`);
      if (isMountedRef.current) setTrainingExamples(res.data.examples || []);
    } catch {
      // Silent
    } finally {
      if (isMountedRef.current) setLoadingTraining(false);
    }
  }, []);

  const fetchTrainingStats = useCallback(async () => {
    if (!isMountedRef.current) return;
    try {
      const res = await axios.get(`${API}/ai-corrections/training-stats`);
      if (isMountedRef.current) setTrainingStats(res.data);
    } catch {
      // Silent
    }
  }, []);

  const fetchReconSummary = useCallback(async () => {
    if (!isMountedRef.current) return;
    try {
      const res = await axios.get(`${API}/bank-reconciliation/summary/all`);
      if (isMountedRef.current) setReconSummary(res.data);
    } catch {
      // Silent
    }
  }, []);

  const fetchDetectedReversals = useCallback(async () => {
    if (!isMountedRef.current) return;
    setLoadingReversals(true);
    try {
      const res = await axios.post(`${API}/bank-reversals/detect-reversals`, {
        days_back: 60,
        amount_tolerance: 0.01,
      });
      if (isMountedRef.current) setDetectedReversals(res.data.matches || []);
    } catch {
      // Silent
    } finally {
      if (isMountedRef.current) setLoadingReversals(false);
    }
  }, []);

  const fetchReversedTransactions = useCallback(async () => {
    if (!isMountedRef.current) return;
    try {
      const res = await axios.get(`${API}/bank-reversals/reversed-transactions?limit=50`);
      if (isMountedRef.current) setReversedTransactions(res.data.transactions || []);
    } catch {
      // Silent
    }
  }, []);

  return {
    // AI corrections
    correctionHistory, setCorrectionHistory,
    suggestedCorrections, setSuggestedCorrections,
    loadingCorrections,
    // AI training
    trainingExamples, setTrainingExamples,
    trainingStats, setTrainingStats,
    loadingTraining,
    // Reconciliation
    reconSummary, setReconSummary,
    // Reversals
    detectedReversals, setDetectedReversals,
    loadingReversals,
    reversedTransactions, setReversedTransactions,
    // Refetchers
    fetchCorrectionHistory,
    fetchSuggestedCorrections,
    fetchTrainingExamples,
    fetchTrainingStats,
    fetchReconSummary,
    fetchDetectedReversals,
    fetchReversedTransactions,
  };
}

export default useBankFeedInsights;
